import { baseEntityReducers, baseInitialState, EntityState, EntitiesResult } from './base/baseModelStore';
import { Chamber, ChambersList, ChamberMaintenanceStatus, ChamberFloodStatus } from '@api-model/models';
import { ModelReducers } from './modelReducers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from '../infrastructure/http/HttpClient';
import store, { AppThunk } from '@root/store/store';
import { dispatchAdditionalActions, fetchAllEntities, AdditionalAction, fetchEntities } from './base/baseModelThunks';
import { Bounds } from 'models/boundsCorners';

import { InvestigationStatus, SiteMaintenanceStatus } from '../api-model/Enums';
import { ChamberSearchParams, PagedChamberSearchParams } from 'models/chamberSearchParams';
import { convertSearchParamsToQueryString } from '@shared/utils/searchParamsConverter';
import { dashboardStats } from './stats';
import { PagedResponseModel } from '@api-model/pagedResponse';
import { sitesListSlice } from './siteList';
import { parseBoundsToCorners } from '@features/dashboard/sites-map/SiteMap.utils';

export interface ChamberEventUpdated {
  siteId: number;
  floodStatus: ChamberFloodStatus;
  maintenanceStatus: ChamberMaintenanceStatus;
  isQuiet: boolean;
  lastEventUpdateDate?: Date;
}

export interface ChamberInvestigation {
  status: InvestigationStatus;
  siteId: number;
}

const updateChamberMaintenance = (
  state: EntityState<Chamber>,
  { payload: { siteId, status } }: PayloadAction<ChamberInvestigation>
) => {
  const chamber = state.entities[siteId];
  chamber.maintenanceStatus = SiteMaintenanceStatus.get(status) ?? ChamberMaintenanceStatus.Unknown;
};

const slice = createSlice({
  name: ModelReducers.chambers,
  initialState: baseInitialState<Chamber>(),
  reducers: {
    ...baseEntityReducers((c) => (c as Chamber).siteId.toString()),
    updateChamberMaintenance,
  },
});

const actions = slice.actions;
const statsActions = dashboardStats.actions;

const setInvestigationStatus =
  (investigation: ChamberInvestigation): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(actions.startLoading());

      let method;
      switch (investigation.status) {
        case InvestigationStatus.ongoing:
          method = 'start';
          break;
        case InvestigationStatus.new:
          method = 'reopen';
          break;
        case InvestigationStatus.cleared:
          method = 'resolve';
          break;
        case InvestigationStatus.maintenance:
          method = 'maintenance';
      }

      await httpClient.post(`Investigation/${method}?siteId=${investigation.siteId}`);
      dispatch(actions.updateChamberMaintenance(investigation));
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const fetchByGeolocation =
  (
    searchParams: ChamberSearchParams,
    bounds: Bounds,
    accountId?: number | null,
    additionalActions?: ((response: Chamber[]) => AdditionalAction)[]
  ): AppThunk =>
  async (dispatch) => {
    const corners = parseBoundsToCorners(bounds);

    try {
      const locationParams = `topLeftLat=${corners.topLeft.lat}&topLeftLng=${corners.topLeft.lng}&bottomRightLat=${corners.bottomRight.lat}&bottomRightLng=${corners.bottomRight.lng}`;
      const queryParams = convertSearchParamsToQueryString(searchParams);

      const url = `Chambers/map?${locationParams}&${queryParams}${accountId ? `&accountId=${accountId}` : ''}`;

      const { data } = await httpClient.get<ChambersList>(url);

      const entities = data?.items ?? [];

      dispatch(
        actions.getEntitiesSuccess({
          items: entities,
          hasAdditionalActions: (additionalActions?.length ?? 0) > 0,
        })
      );

      if (additionalActions) {
        dispatchAdditionalActions(dispatch, additionalActions, entities);
      }
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const search =
  (
    searchParams: PagedChamberSearchParams,
    additionalActions?: ((response: EntitiesResult<Chamber>) => AdditionalAction)[]
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(actions.startLoading());

      const queryParams = convertSearchParamsToQueryString(searchParams);
      const { data } = await httpClient.get<PagedResponseModel<Chamber>>(`chambers?${queryParams}`);

      // When receiving a secondary page, we need to concatenate the entries
      const items = data.page > 1 ? [...Object.values(store.getState().chambers.entities), ...data.items] : data.items;

      // Update site list
      const totalPages = Math.ceil(data.total / data.pageSize);
      dispatch(
        sitesListSlice.actions.setFilterStats({
          currentPage: data.page,
          totalPages: totalPages,
          totalFilteredChambers: data.total,
        })
      );

      const siteIds = data.items.map((chamber) => chamber.siteId);
      if (data.page > 1) {
        dispatch(sitesListSlice.actions.appendVisibleIds(siteIds));
      } else {
        dispatch(sitesListSlice.actions.setVisibleIds(siteIds));
      }

      dispatch(
        actions.getEntitiesSuccess({
          items: items,
          hasAdditionalActions: (additionalActions?.length ?? 0) > 0,
        })
      );

      if (additionalActions) {
        dispatchAdditionalActions(dispatch, additionalActions, data);
      }
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const getLastestUpdatedChambers =
  (
    latestUpdatedChamberDate: string // as Date
  ): AppThunk =>
  async (dispatch) => {
    try {
      const queryParams = new URLSearchParams({} as Record<string, string>);

      queryParams.append('updatedAfter', latestUpdatedChamberDate);

      const { data } = await httpClient.get(`chambers/latest?${queryParams}`);

      const chambers = data.items as Chamber[];
      const latestChamber =
        chambers.length > 0
          ? chambers.sort((a, b) => (a?.lastEventUpdateDate > b?.lastEventUpdateDate ? -1 : 1))[0]
          : null;

      const latestChamberUpdateDate = latestChamber?.lastEventUpdateDate || latestUpdatedChamberDate;

      dispatch(statsActions.setLatestChamberUpdateDate(latestChamberUpdateDate));

      dispatch(
        actions.getEntitiesSuccess({
          items: data.items,
          hasAdditionalActions: false,
        })
      );
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const exportToCsv =
  (searchParams: ChamberSearchParams, resultCallback: (data: EntitiesResult<Chamber>) => void): AppThunk =>
  async (dispatch) => {
    try {
      const queryParams = convertSearchParamsToQueryString(searchParams);

      const { data } = await httpClient.get(`chambers/export?${queryParams}`);

      resultCallback(data);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const fetchAllByText = fetchAllEntities<Chamber>(ModelReducers.chambers, slice.actions, (text?) =>
  httpClient.get(`chambers${text ? '?text=' + text : ''}`)
);
const fetchByIds = fetchEntities<Chamber>(ModelReducers.chambers, slice.actions, (ids) =>
  httpClient.get(`chambers?siteIds=${ids.join('&siteIds=')}`)
);

const thunks = {
  fetchByGeolocation,
  setInvestigationStatus,
  fetchAllByText,
  fetchByIds,
  search,
  exportToCsv,
  getLastestUpdatedChambers,
};

export const chambersEntity = {
  reducer: slice.reducer,
  actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof actions;
  thunks: typeof thunks;
};
